import { AfterViewInit, Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';

import { MainMenuComponent } from '../../shared/main-menu/main-menu.component';
import { AboutMenuComponent } from '../../shared/about-menu/about-menu.component';
import { ScriptLoaderService, VENDOR_SCRIPTS } from '../../script-loader.service';
import { ManagementProfile } from '../../shared/models/management.model';
import { SubscribeComponent } from '../../shared/subscribe/subscribe.component';
import { SheetRow, SheetTableService } from '../services/sheet-table.service';

type ColumnKind = 'location' | 'website' | 'text';


@Component({
  selector: 'app-sri-balaji-educational-society',
  standalone: true,
  imports: [CommonModule, RouterLink, MainMenuComponent, AboutMenuComponent, SubscribeComponent],
  templateUrl: '../templates/sri-balaji-educational-society.component.html',
  styleUrls: ['../css/sheet-table.css']
})
export class SriBalajiEducationalSocietyComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly csvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRa2t7NnTeseSQm3IkU6SDZzLDnLGO-6JcREX515qIMLNXAX-QJkqCAXi46K3dhx5b6IV3Gb-GhMDdo/pub?gid=0&single=true&output=csv';

  readonly profile = signal<ManagementProfile | null>(null);
  readonly rows = signal<SheetRow[]>([]);
  readonly columns = signal<string[]>([]);
  readonly isLoading = signal(true);
  readonly errorMessage = signal('');

  constructor(
    private readonly scriptLoader: ScriptLoaderService,
    private readonly sheetTableService: SheetTableService
  ) { }

  ngOnInit(): void {
    this.loadProfile();
    this.fetchColleges();
  }

  ngAfterViewInit(): void {
    document.body.classList.add('defult-home');
    this.scriptLoader
      .loadSequential(...VENDOR_SCRIPTS)
      .then(() => {
        // If vendor scripts were injected after the native load event,
        // re-dispatch it so jQuery load handlers in main.js still run.
        if (document.readyState === 'complete') {
          window.dispatchEvent(new Event('load'));
        }
      })
      .catch((error) => {
        console.error('Failed to load third-party scripts', error);
      });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('defult-home');
  }

  public getColumnKind(column: string): ColumnKind {
    const normalized = this.normalizeColumnName(column);

    const locationAliases = new Set(['location', 'locationlink', 'map', 'maps']);
    if (locationAliases.has(normalized)) {
      return 'location';
    }

    const websiteAliases = new Set(['website', 'websiteurl', 'web', 'url', 'link', 'site']);
    if (websiteAliases.has(normalized)) {
      return 'website';
    }

    return 'text';
  }

  public getCellUrl(column: string, value: string): string {
    const kind = this.getColumnKind(column);
    const trimmed = value.trim();

    if (!trimmed) {
      return '';
    }

    if (kind === 'location') {
      return `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}`;
    }

    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    return 'https://' + trimmed;
  }

  private normalizeColumnName(name: string): string {
    return name.toLowerCase().replace(/[\s_-]+/g, '');
  }

  private fetchColleges(): void {
    this.sheetTableService
      .loadTableFromCsvUrl(this.csvUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tableData) => {
          this.rows.set(tableData.rows);
          this.columns.set(tableData.columns);
          this.isLoading.set(false);
        },
        error: () => {
          this.errorMessage.set('Unable to load colleges data right now.');
          this.isLoading.set(false);
        }
      });
  }

  private async loadProfile(): Promise<void> {
    try {
      const response = await fetch('https://jntuanotifications.blob.core.windows.net/websites/management-profiles.json');
      if (!response.ok) {
        throw new Error(`Failed to load management profiles: ${response.status}`);
      }

      const profiles = (await response.json()) as ManagementProfile[];
      const profile = profiles.find(p => p.id === 'society');
      this.profile.set(profile || null);
    } catch (error) {
      console.error('Failed to load management profiles JSON', error);
      this.profile.set(null);
    }
  }
}

